import { crmDel, crmGet, crmPost, crmPut } from "../api/api";
import {
  getAccessToken,
  getTenantId,
  getTenantCode,
} from "../utils/sessionDB";
import { getData, getMessage } from "../utils/responseDecoder";

const CRM08_READ = "CRM08S";
const CRM08_ADD = "CRM08A";
const CRM08_DELETE = "CRM08D";
const CRM09_READ = "CRM09S";
const CRM09_UPDATE = "CRM09U";
const CRM10_READ = "CRM10S";
const CRM10_ADD = "CRM10A";
const CRM10_UPDATE = "CRM10U";
const CRM10_DELETE = "CRM10D";
const CRM11_READ = "CRM11S";
const CRM11_ADD = "CRM11A";
const CRM11_UPDATE = "CRM11U";
const CRM11_DELETE = "CRM11D";

function decodePayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.encrypted === true || payload.encrypted === "true") {
    const decoded = getData(JSON.stringify(payload));
    return decoded ?? payload.data ?? payload;
  }
  return payload.data !== undefined ? payload.data : payload;
}

function normalizeResponse(payload) {
  return {
    raw: payload,
    data: decodePayload(payload),
    message: payload.message || "",
  };
}

async function request(method, path, { serviceCode, body } = {}) {
  try {
    let payload;

    switch (method) {
      case "GET":
        payload = await crmGet(path.replace("/saas/crm", ""), serviceCode);
        break;
      case "POST":
        payload = await crmPost(path.replace("/saas/crm", ""), body ?? {}, serviceCode);
        break;
      case "PUT":
        payload = await crmPut(path.replace("/saas/crm", ""), body ?? {}, serviceCode);
        break;
      case "DELETE":
        payload = await crmDel(path.replace("/saas/crm", ""), serviceCode);
        break;
      default:
        throw new Error(`Unsupported request method: ${method}`);
    }

    return normalizeResponse(payload);
  } catch (error) {
    const message =
      error?.message ||
      (typeof error?.data === "string" ? getMessage(error.data) : "") ||
      "Request failed";
    const normalizedError = new Error(message);
    normalizedError.status = error?.status;
    normalizedError.payload = error?.data ?? error?.payload ?? null;
    throw normalizedError;
  }
}

export function hasLiveSalesConfigSession() {
  const token = String(getAccessToken() || "");
  const tenantId = String(getTenantId() || "").trim();
  const tenantCode = String(getTenantCode() || "").trim();
  const normalized = token.replace(/^Bearer\s+/i, "").trim();
  const looksLikeJwt = normalized.split(".").length >= 3;

  return Boolean(normalized) && looksLikeJwt && Boolean(tenantId) && Boolean(tenantCode);
}

export async function getApprovalRules() {
  return request("GET", "/saas/crm/approval-rules/get", {
    serviceCode: CRM08_READ,
  });
}

export async function createApprovalRule(payload) {
  return request("POST", "/saas/crm/approval-rules/add", {
    serviceCode: CRM08_ADD,
    body: payload,
  });
}

export async function deleteApprovalRule(id) {
  return request("DELETE", `/saas/crm/approval-rules/delete/${id}`, {
    serviceCode: CRM08_DELETE,
  });
}

export async function getNetPriceRule() {
  return request("GET", "/saas/crm/net-price-rules/get", {
    serviceCode: CRM09_READ,
  });
}

export async function updateNetPriceRule(payload) {
  return request("PUT", "/saas/crm/net-price-rules/update", {
    serviceCode: CRM09_UPDATE,
    body: payload,
  });
}

export async function getPmcConfigs(quotationType = "") {
  const params = new URLSearchParams();
  const type = String(quotationType || "").trim();
  if (type) {
    params.set("quotationType", type);
  }

  return request("GET", `/saas/crm/pmc-configs/get${params.toString() ? `?${params.toString()}` : ""}`, {
    serviceCode: CRM10_READ,
  });
}

export async function createPmcConfig(payload) {
  return request("POST", "/saas/crm/pmc-configs/add", {
    serviceCode: CRM10_ADD,
    body: payload,
  });
}

export async function updatePmcConfig(id, payload) {
  return request("PUT", `/saas/crm/pmc-configs/update/${id}`, {
    serviceCode: CRM10_UPDATE,
    body: payload,
  });
}

export async function deletePmcConfig(id) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid PMC config id.");
  }

  return request("DELETE", `/saas/crm/pmc-configs/delete/${normalizedId}`, {
    serviceCode: CRM10_DELETE,
  });
}

export async function getTerms() {
  return request("GET", "/saas/crm/terms/get", {
    serviceCode: CRM11_READ,
  });
}

export async function createTerm(payload) {
  return request("POST", "/saas/crm/terms/add", {
    serviceCode: CRM11_ADD,
    body: payload,
  });
}

export async function updateTerm(id, payload) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid term id.");
  }

  return request("PUT", `/saas/crm/terms/update/${normalizedId}`, {
    serviceCode: CRM11_UPDATE,
    body: payload,
  });
}

export async function deleteTerm(id) {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
    throw new Error("Invalid term id.");
  }

  return request("DELETE", `/saas/crm/terms/delete/${normalizedId}`, {
    serviceCode: CRM11_DELETE,
  });
}
